import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Animated,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Svg, { Circle } from "react-native-svg";

import Colors from "../../../../Resources/Color";
import useChevronRotation from "../../../hooks/useChevronRotation";
import {
  normalizeDecimalInput,
  MICRONUTRIENT_FIELDS,
} from "../../recipes/recipeLogic";
import {
  formatCalorieValue,
  formatMacroValue,
  formatMicronutrientValue,
  formatWholePercentageValue,
} from "../../shared/nutritionFormatters";
import {
  PLAN_GOAL_MODE_GRAMS,
  PLAN_GOAL_MODE_PERCENT,
  getGoalTargets,
  getMacroGoalPercentages,
  getObjectiveProgress,
  normalizeGoalDraft,
} from "../nutritionPlanLogic";
import { resolveNutritionPlanDisplayedTotals } from "./nutritionPlanObjectivesTotals";
import { useDevice } from "../../../_layout";
import MultipleSegmentedPicker from "../../../Components/MultipleSegmentedPicker";

const CALORIE_OBJECTIVE = {
  id: "calories",
  labelKey: "energy-string",
  color: Colors.energyColor,
  unit: "kcal",
};

const MACRO_OBJECTIVES = [
  {
    id: "protein",
    labelKey: "protein-string",
    color: Colors.proteinColor,
  },
  {
    id: "carbs",
    labelKey: "carbs-string",
    color: Colors.carbsColor,
  },
  {
    id: "fats",
    labelKey: "fats-string",
    color: Colors.fatsColor,
  },
];

function buildDistributionFromHandles(handleValues) {
  const firstHandle = Math.round(
    Math.max(0, Math.min(100, Number(handleValues?.[0] ?? 0))),
  );
  const secondHandle = Math.max(
    firstHandle,
    Math.round(Math.min(100, Number(handleValues?.[1] ?? 100))),
  );

  return {
    protein: firstHandle,
    carbs: secondHandle - firstHandle,
    fats: 100 - secondHandle,
  };
}

function MacroDistributionChart({ percentages, onChange, t }) {
  const [trackWidth, setTrackWidth] = useState(0);
  const [activeHandleIndex, setActiveHandleIndex] = useState(null);
  const handleValues = useMemo(
    () => [
      Number(percentages?.protein ?? 0),
      Number(percentages?.protein ?? 0) + Number(percentages?.carbs ?? 0),
    ],
    [percentages],
  );

  const resolveHandleIndex = useCallback(
    (locationX) => {
      if (trackWidth <= 0) {
        return 0;
      }

      const nextPercentage = (locationX / trackWidth) * 100;
      return Math.abs(nextPercentage - handleValues[0]) <=
        Math.abs(nextPercentage - handleValues[1])
        ? 0
        : 1;
    },
    [handleValues, trackWidth],
  );

  const updateFromLocation = useCallback(
    (locationX, forcedHandleIndex = activeHandleIndex) => {
      if (trackWidth <= 0 || forcedHandleIndex == null) {
        return;
      }

      const rawValue = Math.round((locationX / trackWidth) * 100);
      const nextHandleValues = [...handleValues];

      if (forcedHandleIndex === 0) {
        nextHandleValues[0] = Math.max(0, Math.min(rawValue, handleValues[1]));
      } else {
        nextHandleValues[1] = Math.max(handleValues[0], Math.min(rawValue, 100));
      }

      onChange(buildDistributionFromHandles(nextHandleValues));
    },
    [activeHandleIndex, handleValues, onChange, trackWidth],
  );

  return (
    <View
      style={styles.distributionTrackArea}
      onLayout={(event) => {
        setTrackWidth(event?.nativeEvent?.layout?.width ?? 0);
      }}
      onStartShouldSetResponder={() => true}
      onMoveShouldSetResponder={() => true}
      onResponderGrant={(event) => {
        const handleIndex = resolveHandleIndex(event?.nativeEvent?.locationX ?? 0);
        setActiveHandleIndex(handleIndex);
        updateFromLocation(event?.nativeEvent?.locationX ?? 0, handleIndex);
      }}
      onResponderMove={(event) => {
        updateFromLocation(event?.nativeEvent?.locationX ?? 0);
      }}
      onResponderRelease={() => setActiveHandleIndex(null)}
      onResponderTerminate={() => setActiveHandleIndex(null)}
      accessibilityRole="adjustable"
      accessibilityLabel={t(
        "nutritionPlanMacroDistributionChartA11y-string",
        "Macro distribution chart",
      )}
    >
      <View style={styles.distributionTrackBase} />
      <View
        style={[
          styles.distributionTrackSegment,
          {
            left: "0%",
            width: `${percentages.protein}%`,
            backgroundColor: Colors.proteinColor,
          },
        ]}
      />
      <View
        style={[
          styles.distributionTrackSegment,
          {
            left: `${percentages.protein}%`,
            width: `${percentages.carbs}%`,
            backgroundColor: Colors.carbsColor,
          },
        ]}
      />
      <View
        style={[
          styles.distributionTrackSegment,
          {
            left: `${percentages.protein + percentages.carbs}%`,
            width: `${percentages.fats}%`,
            backgroundColor: Colors.fatsColor,
          },
        ]}
      />

      {handleValues.map((handleValue, index) => (
        <View
          key={`handle-${index}`}
          pointerEvents="none"
          style={[
            styles.distributionHandle,
            activeHandleIndex === index ? styles.distributionHandleActive : null,
            {
              left: `${handleValue}%`,
            },
          ]}
        />
      ))}
    </View>
  );
}

function ObjectiveProgressRail({ color, progress }) {
  const animatedProgress = useAnimatedValue(progress);

  return (
    <View style={styles.objectiveProgressTrack}>
      <View
        style={[
          styles.objectiveProgressFill,
          {
            backgroundColor: color,
            width: `${animatedProgress}%`,
          },
        ]}
      />
    </View>
  );
}

function useAnimatedValue(targetValue) {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const nextValue = Number.isFinite(Number(targetValue)) ? Number(targetValue) : 0;
    const frame = requestAnimationFrame(() => {
      setAnimatedValue(nextValue);
    });

    return () => cancelAnimationFrame(frame);
  }, [targetValue]);

  return animatedValue;
}

function useAppearTransition() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => cancelAnimationFrame(frame);
  }, []);

  return isVisible;
}

function ModeToggle({ t, activeMode, onChange }) {
  const options = [
    "nutritionPlanGoalsByPercentage-string",
    "nutritionPlanGoalsByGrams-string",
  ];
  const selectedIndex =
    activeMode === PLAN_GOAL_MODE_GRAMS ? 1 : 0;

  return (
    <MultipleSegmentedPicker
      options={options}
      value={selectedIndex}
      onChange={(index) =>
        onChange(index === 1 ? PLAN_GOAL_MODE_GRAMS : PLAN_GOAL_MODE_PERCENT)
      }
    />
  );
}

function ProgressRing({
  color,
  label,
  currentValue,
  targetValue,
  progress,
  completionLabel,
  isHovered,
  isDimmed,
  isInteractive,
  onHoverStart,
  onHoverEnd,
}) {
  const size = 84;
  const strokeWidth = 8;
  const overflowStrokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const overflowRadius = radius + 8;
  const circumference = 2 * Math.PI * radius;
  const overflowCircumference = 2 * Math.PI * overflowRadius;
  const cappedProgress = Math.max(0, Math.min(100, Number(progress ?? 0)));
  const rawCompletion = Math.max(0, Number(completionLabel ?? 0));
  const overflow = Math.max(0, rawCompletion - 100);
  const animatedProgress = useAnimatedValue(cappedProgress);
  const animatedOverflow = useAnimatedValue(Math.min(overflow, 100));
  const isVisible = useAppearTransition();
  const dashOffset = circumference * (1 - animatedProgress / 100);
  const overflowOffset =
    overflowCircumference * (1 - animatedOverflow / 100);

  return (
    <View
      onMouseEnter={isInteractive ? onHoverStart : undefined}
      onMouseLeave={isInteractive ? onHoverEnd : undefined}
      style={[
        styles.progressRingCard,
        isVisible ? styles.chartCardVisible : styles.chartCardHidden,
        isHovered ? styles.progressRingCardActive : null,
        isDimmed ? styles.chartCardMuted : null,
      ]}
    >
      <Text style={styles.progressRingLabel}>{label}</Text>

      <View style={styles.progressRingWrap}>
        <Svg width={size + 18} height={size + 18} viewBox={`0 0 ${size + 18} ${size + 18}`}>
          <Circle
            cx={(size + 18) / 2}
            cy={(size + 18) / 2}
            r={overflowRadius}
            stroke={Colors.hoveredGray}
            strokeWidth={overflowStrokeWidth}
            fill="none"
          />
          {overflow > 0 ? (
            <Circle
              cx={(size + 18) / 2}
              cy={(size + 18) / 2}
              r={overflowRadius}
              stroke={color}
              strokeOpacity={0.45}
              strokeWidth={overflowStrokeWidth}
              strokeLinecap="round"
              strokeDasharray={overflowCircumference}
              strokeDashoffset={overflowOffset}
              fill="none"
              style={styles.progressRingCircle}
              originX={(size + 18) / 2}
              originY={(size + 18) / 2}
              rotation="-90"
            />
          ) : null}
          <Circle
            cx={(size + 18) / 2}
            cy={(size + 18) / 2}
            r={radius}
            stroke={Colors.lightGrayColor}
            strokeWidth={strokeWidth}
            fill="none"
          />
          <Circle
            cx={(size + 18) / 2}
            cy={(size + 18) / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            fill="none"
            style={styles.progressRingCircle}
            originX={(size + 18) / 2}
            originY={(size + 18) / 2}
            rotation="-90"
          />
        </Svg>

        <View style={styles.progressRingCenter}>
          <Text style={styles.progressRingValue}>
            {formatWholePercentageValue(rawCompletion)}%
          </Text>
          {overflow > 0 ? (
            <Text style={styles.progressRingOverflow}>
              +{formatWholePercentageValue(overflow)}%
            </Text>
          ) : null}
        </View>
      </View>

      <Text style={styles.progressRingMeta}>
        {formatMacroValue(currentValue)} / {formatMacroValue(targetValue)} g
      </Text>
    </View>
  );
}

function getProgressMetaCopy({ t, current, target, unit, formatValue }) {
  if (Number(target ?? 0) <= 0) {
    return `${t("current-string", "Current")} ${formatValue(current)} ${unit}`;
  }

  return `${formatValue(current)} / ${formatValue(target)} ${unit}`;
}

function getEnergyStatusMeta(t, calorieProgress) {
  const current = Number(calorieProgress?.current ?? 0);
  const target = Number(calorieProgress?.target ?? 0);

  if (target <= 0) {
    return null;
  }

  const delta = Math.abs(target - current);

  if (current <= target) {
    return {
      label: t(
        "nutritionPlanEnergyRemaining-string",
        "{{value}} kcal remaining",
        { value: formatCalorieValue(delta) },
      ),
      tone: "remaining",
    };
  }

  return {
    label: t(
      "nutritionPlanEnergyExceeded-string",
      "{{value}} kcal exceeded",
      { value: formatCalorieValue(delta) },
    ),
    tone: "exceeded",
  };
}

function GoalModeEditor({
  t,
  goalMode,
  draftGoals,
  goalTargets,
  macroPercentages,
  macroRows,
  onDoneEditing,
  onCalorieChange,
  onMacroPercentagesChange,
  onMacroGramsChange,
  onGoalModeChange,
}) {
  const { isMobile } = useDevice();
  const isPercentMode = goalMode === PLAN_GOAL_MODE_PERCENT;
  const [isDoneHovered, setIsDoneHovered] = useState(false);

  return (
    <View style={styles.editorState}>
      <View style={styles.editorToolbarRow}>
        <ModeToggle t={t} activeMode={goalMode} onChange={onGoalModeChange} />
        <TouchableOpacity
          activeOpacity={0.82}
          onPress={onDoneEditing}
          onMouseEnter={!isMobile ? () => setIsDoneHovered(true) : undefined}
          onMouseLeave={!isMobile ? () => setIsDoneHovered(false) : undefined}
          style={[styles.doneButton, isDoneHovered ? styles.doneButtonHovered : null]}
        >
          <Text style={styles.doneButtonText}>{t("done-string", "Done")}</Text>
        </TouchableOpacity>
      </View>

      {isPercentMode ? (
        <View style={[styles.editorCard, styles.percentEditorCard]}>
          <Text
            style={[
              styles.helperText,
              styles.percentEditorTextBlock,
              styles.percentEditorHelperText,
            ]}
          >
            {t(
              "nutritionPlanObjectivesPercentModeHint-string",
              "Set the calories, then drag the sliders to define the desired percentages.",
            )}
          </Text>

          <View style={[styles.metricCard, styles.percentEditorTextBlock]}>
            <View style={styles.metricHeaderRow}>
              <View style={styles.metricHeading}>
                <Text style={styles.metricLabel}>{t(CALORIE_OBJECTIVE.labelKey)}</Text>
              </View>
              <View style={[styles.metricInputWrap, styles.percentEnergyInputWrap]}>
                <TextInput
                  value={draftGoals?.nutrients?.calories ?? ""}
                  onChangeText={(value) => onCalorieChange(normalizeDecimalInput(value))}
                  placeholder="0"
                  placeholderTextColor={Colors.grayColor}
                  keyboardType="numeric"
                  style={[styles.metricInput, styles.percentEnergyInput]}
                  accessibilityLabel={t(CALORIE_OBJECTIVE.labelKey)}
                  aria-label={t(CALORIE_OBJECTIVE.labelKey)}
                />
                <Text style={styles.metricInputUnit}>{CALORIE_OBJECTIVE.unit}</Text>
              </View>
            </View>
          </View>

          <View style={[styles.sliderBlock, styles.percentEditorTextBlock]}>
            <Text style={styles.sliderLabel}>
              {t("nutritionPlanMacroSplitLabel-string", "Macro split")}
            </Text>
            <MacroDistributionChart
              percentages={macroPercentages}
              onChange={onMacroPercentagesChange}
              t={t}
            />
          </View>

          <View
            style={[
              styles.macroEditorList,
              styles.percentEditorMacroList,
              styles.percentEditorTextBlock,
            ]}
          >
            {macroRows.map((macro) => (
              <View key={macro.id} style={styles.editorMacroRow}>
                <View style={styles.percentEditorMacroTitleRow}>
                  <View
                    style={[
                      styles.objectiveDot,
                      styles.percentEditorMacroDot,
                      { backgroundColor: macro.color },
                    ]}
                  />
                  <Text style={styles.macroLabel}>
                    {macro.label}
                  </Text>
                  <Text style={styles.percentEditorMacroSeparator}>·</Text>
                  <Text style={styles.macroPercentLabel}>
                    {formatWholePercentageValue(macro.percentage)}%
                  </Text>
                </View>

                <View
                  style={[
                    styles.editorMacroValues,
                    styles.percentEditorMacroValues,
                  ]}
                >
                  <Text
                    style={[
                      styles.editorDerivedValue,
                      styles.percentEditorDerivedValue,
                    ]}
                  >
                    {formatMacroValue(macro.targetValue)} g
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      ) : (
        <View style={[styles.editorCard, styles.gramEditorCard]}>
          <Text
            style={[
              styles.helperText,
              styles.gramEditorTextBlock,
              styles.gramEditorHelperText,
            ]}
          >
            {t(
              "nutritionPlanObjectivesGramModeHint-string",
              "Set the grams for protein, carbs, and fats. Total calories are calculated automatically from those values.",
            )}
          </Text>

          <View style={[styles.metricCard, styles.gramEditorTextBlock]}>
            <View style={styles.metricHeaderRow}>
              <View style={styles.metricHeading}>
                <Text style={styles.metricLabel}>{t(CALORIE_OBJECTIVE.labelKey)}</Text>
              </View>

              <View style={styles.derivedMetricValueWrap}>
                <Text
                  style={[
                    styles.derivedMetricValue,
                    styles.gramDerivedMetricValue,
                  ]}
                >
                  {formatCalorieValue(goalTargets?.calories)}
                </Text>
                <Text style={styles.metricInputUnit}>kcal</Text>
              </View>
            </View>

          </View>

          <View style={[styles.macroEditorList, styles.gramEditorTextBlock]}>
            {macroRows.map((macro) => (
              <View key={macro.id} style={styles.editorMacroRow}>
                <View style={styles.percentEditorMacroTitleRow}>
                  <View
                    style={[
                      styles.objectiveDot,
                      styles.percentEditorMacroDot,
                      { backgroundColor: macro.color },
                    ]}
                  />
                  <Text style={styles.macroLabel}>{macro.label}</Text>
                  <Text style={styles.percentEditorMacroSeparator}>·</Text>
                  <Text style={styles.macroPercentLabel}>
                    {formatWholePercentageValue(macro.percentage)}%
                  </Text>
                </View>

                <View style={styles.editorMacroValues}>
                  <View style={[styles.metricInputWrap, styles.gramInputWrap]}>
                    <TextInput
                      value={draftGoals?.nutrients?.[macro.id] ?? ""}
                      onChangeText={(value) =>
                        onMacroGramsChange(macro.id, normalizeDecimalInput(value))
                      }
                      placeholder="0"
                      placeholderTextColor={Colors.grayColor}
                      keyboardType="numeric"
                      style={[styles.metricInput, styles.gramMacroInput]}
                      accessibilityLabel={t(
                        "nutritionPlanMacroGramsInputA11y-string",
                        "{{label}} grams",
                        { label: macro.label },
                      )}
                      aria-label={t(
                        "nutritionPlanMacroGramsInputA11y-string",
                        "{{label}} grams",
                        { label: macro.label },
                      )}
                    />
                    <Text style={styles.metricInputUnit}>g</Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

function GoalProgressView({ t, calorieProgress, macroRows }) {
  const { isMobile } = useDevice();
  const energyStatus = getEnergyStatusMeta(t, calorieProgress);
  const [hoveredChart, setHoveredChart] = useState(null);
  const isVisible = useAppearTransition();
  const isEnergyHovered = hoveredChart === "energy";
  const hasHoveredMacro = hoveredChart != null && hoveredChart !== "energy";

  return (
    <View style={styles.progressState}>
      <View
        onMouseEnter={!isMobile ? () => setHoveredChart("energy") : undefined}
        onMouseLeave={!isMobile ? () => setHoveredChart(null) : undefined}
        style={[
          styles.energyProgressCard,
          isVisible ? styles.chartCardVisible : styles.chartCardHidden,
          isEnergyHovered ? styles.energyProgressCardActive : null,
          hasHoveredMacro ? styles.chartCardMuted : null,
        ]}
      >
        <View style={styles.energyProgressHeaderRow}>
          <Text style={styles.progressSectionLabel}>{t("energy-string", "Energy")}</Text>
          {energyStatus ? (
            <Text
              style={styles.energyStatusText}
            >
              {energyStatus.label}
            </Text>
          ) : null}
        </View>
        <ObjectiveProgressRail
          color={CALORIE_OBJECTIVE.color}
          progress={calorieProgress.progress}
        />
        <Text style={styles.energyProgressValue}>
          {getProgressMetaCopy({
            t,
            current: calorieProgress.current,
            target: calorieProgress.target,
            unit: "kcal",
            formatValue: formatCalorieValue,
          })}
        </Text>
      </View>

      <View style={styles.progressRingGrid}>
        {macroRows.map((macro) => (
          <ProgressRing
            key={macro.id}
            color={macro.color}
            label={macro.label}
            currentValue={macro.currentValue}
            targetValue={macro.targetValue}
            progress={macro.progress.progress}
            completionLabel={macro.progress.completionLabel}
            isHovered={hoveredChart === macro.id}
            isDimmed={hoveredChart != null && hoveredChart !== macro.id}
            isInteractive={!isMobile}
            onHoverStart={() => setHoveredChart(macro.id)}
            onHoverEnd={() => setHoveredChart(null)}
          />
        ))}
      </View>
    </View>
  );
}

export function NutritionPlanObjectivesSection({
  t,
  draftGoals,
  currentTotals,
  loadingFallbackTotals = null,
  isCurrentTotalsLoading = false,
  isEditingGoals,
  onDoneEditing,
  onCalorieChange,
  onMacroPercentagesChange,
  onMacroGramsChange,
  onGoalModeChange,
}) {
  const { isMobile } = useDevice();
  const normalizedGoals = useMemo(() => normalizeGoalDraft(draftGoals), [draftGoals]);
  const goalTargets = useMemo(() => getGoalTargets(normalizedGoals), [normalizedGoals]);
  const [microsExpanded, setMicrosExpanded] = useState(false);
  const [isMicrosToggleHovered, setIsMicrosToggleHovered] = useState(false);
  const microsChevronRotation = useChevronRotation(
    microsExpanded,
    "closedDownOpenUp",
  );
  const displayTotals = useMemo(
    () =>
      resolveNutritionPlanDisplayedTotals({
        currentTotals,
        loadingFallbackTotals,
        isCurrentTotalsLoading,
      }),
    [currentTotals, isCurrentTotalsLoading, loadingFallbackTotals],
  );
  const macroPercentages = useMemo(
    () => getMacroGoalPercentages(normalizedGoals, { fallbackToDefault: true }),
    [normalizedGoals],
  );
  const micronutrientSummaryItems = useMemo(
    () =>
      MICRONUTRIENT_FIELDS.map((field) => ({
        ...field,
        label: t(field.labelKey),
        value: Number(displayTotals?.micros?.[field.key] ?? 0),
      })),
    [displayTotals?.micros, t],
  );
  const calorieProgress = useMemo(
    () =>
      getObjectiveProgress(
        Number(displayTotals?.calories ?? 0),
        Number(goalTargets?.calories ?? 0),
      ),
    [displayTotals?.calories, goalTargets?.calories],
  );
  const macroRows = useMemo(
    () =>
      MACRO_OBJECTIVES.map((macro) => {
        const currentValue = Number(displayTotals?.[macro.id] ?? 0);
        const targetValue = Number(goalTargets?.[macro.id] ?? 0);
        const progress = getObjectiveProgress(currentValue, targetValue);

        return {
          ...macro,
          label: t(macro.labelKey),
          percentage: Number(macroPercentages?.[macro.id] ?? 0),
          currentValue,
          targetValue,
          progress,
        };
      }),
    [displayTotals, goalTargets, macroPercentages, t],
  );

  return (
    <View style={styles.container}>
      {isEditingGoals ? (
        <GoalModeEditor
          t={t}
          goalMode={normalizedGoals.macro_input_mode}
          draftGoals={normalizedGoals}
          goalTargets={goalTargets}
          macroPercentages={macroPercentages}
          macroRows={macroRows}
          onDoneEditing={onDoneEditing}
          onCalorieChange={onCalorieChange}
          onMacroPercentagesChange={onMacroPercentagesChange}
          onMacroGramsChange={onMacroGramsChange}
          onGoalModeChange={onGoalModeChange}
        />
      ) : (
        <GoalProgressView
          t={t}
          calorieProgress={calorieProgress}
          macroRows={macroRows}
        />
      )}

      {!isEditingGoals ? (
        <>
          <View style={styles.fiberCard}>
            <View style={styles.fiberHeader}>
              <Text style={styles.fiberLabel}>{t("fiber-string")}</Text>
              <Text style={styles.fiberValue}>
                {formatMacroValue(displayTotals?.fiber)}
                <Text style={styles.fiberUnit}> g</Text>
              </Text>
            </View>
          </View>

          <View style={styles.summaryMicrosCard}>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => setMicrosExpanded((current) => !current)}
              onMouseEnter={
                !isMobile ? () => setIsMicrosToggleHovered(true) : undefined
              }
              onMouseLeave={
                !isMobile ? () => setIsMicrosToggleHovered(false) : undefined
              }
              style={styles.summaryMicrosToggle}
            >
              <View style={styles.summaryMicrosToggleMain}>
                <Text
                  style={[
                    styles.summaryMicrosTitle,
                    isMicrosToggleHovered ? styles.summaryMicrosTitleHovered : null,
                  ]}
                >
                  {t("addFoodModalMicrosTitle-string")}
                </Text>
              </View>
              <Animated.View
                style={[
                  styles.summaryMicrosChevron,
                  { transform: [{ rotate: microsChevronRotation }] },
                ]}
              >
                <Ionicons
                  name="chevron-down"
                  size={14}
                  color={
                    isMicrosToggleHovered ? Colors.darkgrayColor : Colors.grayColor
                  }
                />
              </Animated.View>
            </TouchableOpacity>

            {microsExpanded ? (
              <View style={styles.summaryMicroList}>
                {micronutrientSummaryItems.map((item, index) => (
                  <View
                    key={item.key}
                    style={[
                      styles.summaryMicroRow,
                      index !== 0 ? styles.summaryMicroRowBorder : null,
                    ]}
                  >
                    <Text style={styles.summaryMicroLabel}>{item.label}</Text>
                    <Text style={styles.summaryMicroValue}>
                      {formatMicronutrientValue(item.value)}
                      <Text style={styles.summaryMicroUnit}> {item.unit}</Text>
                    </Text>
                  </View>
                ))}
              </View>
            ) : null}
          </View>
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 14,
  },
  editorState: {
    gap: 12,
  },
  editorToolbarRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  progressState: {
    gap: 6,
  },
  editorCard: {
    gap: 14,
    padding: 14,
    borderRadius: 14,
    backgroundColor: Colors.whiteColor,
  },
  percentEditorCard: {
    paddingHorizontal: 0,
  },
  percentEditorTextBlock: {
    paddingHorizontal: 0,
  },
  percentEditorHelperText: {
    marginTop: -8,
  },
  gramEditorCard: {
    paddingHorizontal: 0,
  },
  gramEditorTextBlock: {
    paddingHorizontal: 0,
  },
  gramEditorHelperText: {
    marginTop: -8,
  },
  energyProgressCard: {
    gap: 10,
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: Colors.whiteColor,
    transition: "opacity 260ms ease, transform 320ms ease",
  },
  chartCardHidden: {
    opacity: 0,
    transform: [{ translateY: 6 }, { scale: 0.985 }],
  },
  chartCardVisible: {
    opacity: 1,
    transform: [{ translateY: 0 }, { scale: 1 }],
  },
  chartCardMuted: {
    opacity: 0.58,
  },
  energyProgressCardActive: {
    transform: [{ translateY: -1 }, { scale: 1.008 }],
  },
  energyProgressHeaderRow: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  progressSectionLabel: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  helperText: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.grayColor,
  },
  modeToggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 4,
    borderRadius: 12,
    alignSelf: "flex-start",
    backgroundColor: Colors.lightGrayColor,
  },
  doneButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 0.5,
    borderColor: Colors.bubbleColor,
    borderRadius: 10,
  },
  doneButtonHovered: {
    opacity: 0.7,
  },
  doneButtonText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.bubbleColor,
  },
  modeToggleOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 9,
  },
  modeToggleOptionActive: {
    backgroundColor: Colors.whiteColor,
  },
  modeToggleLabel: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  modeToggleLabelActive: {
    color: Colors.bubbleColor,
  },
  metricCard: {
    gap: 10,
  },
  metricHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  metricHeading: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  objectiveDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  metricLabel: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  metricInputWrap: {
    minWidth: 52,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
    minHeight: 34,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 0.5,
    borderRadius: 10,
    borderColor: Colors.borderColor,
    backgroundColor: "transparent",
  },
  gramInputWrap: {
    minWidth: 0,
  },
  percentEnergyInputWrap: {
    minWidth: 0,
  },
  metricInput: {
    minWidth: 18,
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
    outlineStyle: "none",
    textAlign: "right",
    backgroundColor: "transparent",
    paddingVertical: 0,
  },
  percentEnergyInput: {
    width: 40,
    minWidth: 40,
  },
  gramMacroInput: {
    width: 40,
    minWidth: 40,
  },
  metricInputUnit: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  derivedMetricValueWrap: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 6,
  },
  derivedMetricValue: {
    fontSize: 18,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  gramDerivedMetricValue: {
    fontSize: 16,
    color: Colors.blackColor,
  },
  macroEditorList: {
    gap: 10,
  },
  percentEditorMacroList: {
    gap: 18,
  },
  editorMacroRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingVertical: 2,
    flexWrap: "wrap",
  },
  macroTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  macroLabel: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  percentEditorMacroTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
  percentEditorMacroDot: {
    marginRight: 2,
  },
  percentEditorMacroSeparator: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  editorMacroValues: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginLeft: "auto",
  },
  percentEditorMacroValues: {
    gap: 4,
  },
  percentEditorDerivedValue: {
    minWidth: 0,
    textAlign: "left",
  },
  macroPercentageWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  macroPercentLabel: {
    fontSize: 12,
    fontWeight: "600",
    textAlign: "left",
    color: Colors.blackColor,
  },
  editorDerivedValue: {
    minWidth: 54,
    fontSize: 12,
    color: Colors.blackColor,
    textAlign: "right",
  },
  sliderBlock: {
    gap: 8,
    paddingTop: 4,
  },
  sliderLabel: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  distributionTrackArea: {
    position: "relative",
    height: 34,
    justifyContent: "center",
    paddingVertical: 8,
  },
  distributionTrackBase: {
    height: 8,
    borderRadius: 999,
    backgroundColor: Colors.lightGrayColor,
  },
  distributionTrackSegment: {
    position: "absolute",
    top: 13,
    height: 8,
    borderRadius: 999,
  },
  distributionHandle: {
    position: "absolute",
    top: 7,
    width: 18,
    height: 18,
    borderRadius: 999,
    backgroundColor: Colors.whiteColor,
    borderWidth: 2,
    borderColor: Colors.darkgrayColor,
    transform: [{ translateX: -9 }],
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  distributionHandleActive: {
    borderColor: Colors.bubbleColor,
    transform: [{ translateX: -9 }, { scale: 1.05 }],
  },
  objectiveProgressTrack: {
    width: "100%",
    height: 8,
    borderRadius: 999,
    backgroundColor: Colors.lightGrayColor,
    overflow: "hidden",
  },
  objectiveProgressFill: {
    height: "100%",
    borderRadius: 999,
    transition: "width 420ms cubic-bezier(0.2, 0.8, 0.2, 1)",
  },
  energyProgressValue: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  energyStatusText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  progressRingGrid: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  progressRingCard: {
    flexGrow: 1,
    flexBasis: 96,
    minWidth: 96,
    alignItems: "center",
    gap: 8,
    paddingVertical: 12,
    borderRadius: 14,
    backgroundColor: Colors.whiteColor,
    transition: "opacity 260ms ease, transform 320ms ease",
  },
  progressRingCardActive: {
    transform: [{ translateY: -1 }, { scale: 1.012 }],
  },
  progressRingLabel: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  progressRingWrap: {
    width: 96,
    height: 96,
    alignItems: "center",
    justifyContent: "center",
  },
  progressRingCenter: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  },
  progressRingValue: {
    fontSize: 15,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  progressRingCircle: {
    transition:
      "stroke-dashoffset 420ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 260ms ease",
  },
  progressRingOverflow: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.warningTextColor,
  },
  progressRingMeta: {
    fontSize: 11,
    color: Colors.blackColor,
    textAlign: "center",
  },
  fiberCard: {
    paddingTop: 10,
    backgroundColor: Colors.whiteColor,
  },
  fiberHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  fiberLabel: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  fiberValue: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  fiberUnit: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  summaryMicrosCard: {
    marginTop: 2,
  },
  summaryMicrosToggle: {
    minHeight: 28,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  summaryMicrosToggleMain: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  summaryMicrosTitle: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.grayColor,
    transition: "color 180ms ease-in-out",
  },
  summaryMicrosTitleHovered: {
    color: Colors.darkgrayColor,
  },
  summaryMicrosChevron: {
    transition: "color 180ms ease-in-out",
  },
  summaryMicroList: {
    marginTop: 10,
  },
  summaryMicroRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 2,
    paddingVertical: 8,
  },
  summaryMicroRowBorder: {
    borderTopWidth: 0.5,
    borderTopColor: Colors.borderColor,
  },
  summaryMicroLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  summaryMicroValue: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.blackColor,
    textAlign: "right",
  },
  summaryMicroUnit: {
    fontSize: 10,
    fontWeight: "400",
    color: Colors.grayColor,
  },
});
